import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Autocomplete, Box, Button, Grid, MenuItem, TextField, Typography } from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LocalTaxiOutlinedIcon from '@mui/icons-material/LocalTaxiOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SosOutlinedIcon from '@mui/icons-material/SosOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import { calcEst, optionsRequired, showEst } from 'common/sharedFunctions';
import { DENSE_MIN_DRIVERS, DENSE_MIN_DRIVERS_RANGE } from 'common/src/other/nearbyDrivers';
import {
  REQUEST_TIMEOUT_DEFAULT_SEC,
  REQUEST_TIMEOUT_MIN_SEC,
  REQUEST_TIMEOUT_MAX_SEC,
} from 'common/src/other/requestTimeout';
import {
  DEFAULT_WAVE2_AFTER_MIN,
  DEFAULT_WAVE2_FACTOR,
  DEFAULT_WAVE3_AFTER_MIN,
  DEFAULT_WAVE3_FACTOR,
  DRIVER_RADIUS_RANGE,
  WAVE_AFTER_MIN_RANGE,
  resolveDriverRadius,
} from 'common/src/other/dispatchWaves';
import { C2, C2_FONT } from '../components/Table/C2UserTable';
import {
  DemoModeAlert,
  SettingToggle,
  SettingsCard,
  SettingsField,
  SettingsNotice,
  SettingsSaveBar,
  isAmountList,
  isNonNegativeNumber,
  normalizeAmountList,
  settingsFieldSx,
  useSettingsDraft,
} from '../components/settings/C2SettingsKit';

const NUMBER_FIELDS = [
  'bonus',
  'decimal',
  'driverRadius',
  'dispatchWave2Radius',
  'dispatchWave2AfterMin',
  'dispatchWave3Radius',
  'dispatchWave3AfterMin',
  'driverThreshold',
  'bidprice',
  'denseMinDrivers',
  'requestTimeoutSec',
  'smartFarePeakMult',
  'smartFareNightMult',
];
const SMART_FARE_MULT_MIN = 1;
const SMART_FARE_MULT_MAX = 2;
const GEOHASH_READY_PCT = 90;

const validate = (d) => {
  const e = {};
  if (!d) return e;
  if (!String(d.symbol || '').trim()) e.symbol = 'Obligatorio.';
  if (!String(d.code || '').trim()) e.code = 'Obligatorio.';
  const dec = Number(d.decimal);
  if (d.decimal === '' || d.decimal === undefined || !Number.isInteger(dec) || dec < 0 || dec > 4) e.decimal = 'Entero entre 0 y 4.';
  if (!isNonNegativeNumber(d.bonus)) e.bonus = 'Escribe un valor igual o mayor a 0.';
  {
    const r = Number(d.driverRadius);
    if (!(r > 0) || r < DRIVER_RADIUS_RANGE.min || r > DRIVER_RADIUS_RANGE.max) {
      e.driverRadius = `Entre ${DRIVER_RADIUS_RANGE.min} y ${DRIVER_RADIUS_RANGE.max}.`;
    }
  }
  const validateWaveRadius = (key, label) => {
    if (d[key] === '' || d[key] === undefined || d[key] === null) return;
    const r = Number(d[key]);
    if (!(r > 0) || r < DRIVER_RADIUS_RANGE.min || r > DRIVER_RADIUS_RANGE.max) {
      e[key] = `${label}: entre ${DRIVER_RADIUS_RANGE.min} y ${DRIVER_RADIUS_RANGE.max}.`;
    }
  };
  const validateWaveAfter = (key, label) => {
    if (d[key] === '' || d[key] === undefined || d[key] === null) return;
    const n = Number(d[key]);
    if (!Number.isInteger(n) || n < WAVE_AFTER_MIN_RANGE.min || n > WAVE_AFTER_MIN_RANGE.max) {
      e[key] = `${label}: entero ${WAVE_AFTER_MIN_RANGE.min}–${WAVE_AFTER_MIN_RANGE.max} min.`;
    }
  };
  validateWaveRadius('dispatchWave2Radius', 'Radio ola 2');
  validateWaveRadius('dispatchWave3Radius', 'Radio ola 3');
  validateWaveAfter('dispatchWave2AfterMin', 'Minutos ola 2');
  validateWaveAfter('dispatchWave3AfterMin', 'Minutos ola 3');
  {
    const w2 = d.dispatchWave2AfterMin === '' || d.dispatchWave2AfterMin == null
      ? DEFAULT_WAVE2_AFTER_MIN
      : Number(d.dispatchWave2AfterMin);
    const w3 = d.dispatchWave3AfterMin === '' || d.dispatchWave3AfterMin == null
      ? DEFAULT_WAVE3_AFTER_MIN
      : Number(d.dispatchWave3AfterMin);
    if (Number.isFinite(w2) && Number.isFinite(w3) && w3 < w2) {
      e.dispatchWave3AfterMin = 'La ola 3 debe empezar a la vez o después que la ola 2.';
    }
  }
  if (d.driverThreshold !== '' && d.driverThreshold !== undefined && d.driverThreshold !== null && isNaN(Number(d.driverThreshold))) {
    e.driverThreshold = 'Debe ser un número.';
  }
  if (!isAmountList(d.walletMoneyField)) e.walletMoneyField = 'Montos positivos separados por coma.';
  if (!isAmountList(d.tipMoneyField)) e.tipMoneyField = 'Montos positivos separados por coma.';
  if (d.bidprice !== '' && d.bidprice !== undefined && d.bidprice !== null && !isNonNegativeNumber(d.bidprice)) e.bidprice = 'Escribe un valor igual o mayor a 0.';
  if (!d.emailLogin && !d.mobileLogin) {
    e.emailLogin = 'Debe quedar activo el inicio con correo o con celular.';
    e.mobileLogin = e.emailLogin;
  }
  if (String(d.panic || '').trim() && !/^\+?[\d\s-]{2,20}$/.test(String(d.panic).trim())) e.panic = 'Solo números, por ejemplo 123.';
  if (d.denseMinDrivers !== '' && d.denseMinDrivers !== undefined && d.denseMinDrivers !== null) {
    const n = Number(d.denseMinDrivers);
    if (!Number.isInteger(n) || n < DENSE_MIN_DRIVERS_RANGE.min || n > DENSE_MIN_DRIVERS_RANGE.max) {
      e.denseMinDrivers = `Entero entre ${DENSE_MIN_DRIVERS_RANGE.min} y ${DENSE_MIN_DRIVERS_RANGE.max}.`;
    }
  }
  if (d.smartFarePeakMult !== '' && d.smartFarePeakMult !== undefined && d.smartFarePeakMult !== null) {
    const m = Number(d.smartFarePeakMult);
    if (!Number.isFinite(m) || m < SMART_FARE_MULT_MIN || m > SMART_FARE_MULT_MAX) {
      e.smartFarePeakMult = `Entre ${SMART_FARE_MULT_MIN} y ${SMART_FARE_MULT_MAX}.`;
    }
  }
  if (d.smartFareNightMult !== '' && d.smartFareNightMult !== undefined && d.smartFareNightMult !== null) {
    const m = Number(d.smartFareNightMult);
    if (!Number.isFinite(m) || m < SMART_FARE_MULT_MIN || m > SMART_FARE_MULT_MAX) {
      e.smartFareNightMult = `Entre ${SMART_FARE_MULT_MIN} y ${SMART_FARE_MULT_MAX}.`;
    }
  }
  {
    const raw = d.requestTimeoutSec;
    const n = raw === '' || raw === undefined || raw === null
      ? REQUEST_TIMEOUT_DEFAULT_SEC
      : Number(raw);
    if (!Number.isInteger(n) || n < REQUEST_TIMEOUT_MIN_SEC || n > REQUEST_TIMEOUT_MAX_SEC) {
      e.requestTimeoutSec = `Entero entre ${REQUEST_TIMEOUT_MIN_SEC} y ${REQUEST_TIMEOUT_MAX_SEC} segundos.`;
    }
  }
  return e;
};

const normalize = (changes) => {
  const out = {};
  Object.keys(changes).forEach((k) => {
    let v = changes[k];
    if (k === 'requestTimeoutSec') {
      v = v === '' || v === null || v === undefined
        ? REQUEST_TIMEOUT_DEFAULT_SEC
        : Number(v);
    } else if (NUMBER_FIELDS.includes(k)) {
      v = v === '' || v === null || v === undefined ? null : Number(v);
    } else if (k === 'walletMoneyField' || k === 'tipMoneyField') {
      v = normalizeAmountList(v);
    } else if (typeof v === 'string') {
      v = v.trim();
    }
    out[k] = v;
  });
  return out;
};

const numberInput = { inputMode: 'decimal' };

/** Columna con dos tarjetas: SettingsCard usa height 100%, y sin esto la segunda se desborda debajo de la fila siguiente. */
const stackedColumnSx = { display: 'flex', flexDirection: 'column' };

const GeneralSettings = ({ onDirtyChange }) => {
  const { editSettings, countries, fetchDrivers, editDeepseekApiKey } = api;
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const savedDeepseekKey = useSelector(
    (state) => state.fareMarketAdvisor?.deepseekApiKey || state.fareMarketAdvisor?.geminiApiKey || ''
  );
  const { draft, setDraft, setField, changes, dirty, discard } = useSettingsDraft(settings);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [geoAdoption, setGeoAdoption] = useState(null);
  const [checkingAdoption, setCheckingAdoption] = useState(false);
  const [deepseekApiKey, setDeepseekApiKey] = useState('');
  const [deepseekKeyFocused, setDeepseekKeyFocused] = useState(false);
  const canEdit = !!settings?.AllowCriticalEditsAdmin;
  const errors = useMemo(() => validate(draft), [draft]);
  const deepseekDirty = deepseekApiKey !== savedDeepseekKey;
  const formDirty = dirty || deepseekDirty;

  useEffect(() => {
    if (!deepseekKeyFocused) setDeepseekApiKey(savedDeepseekKey || '');
  }, [savedDeepseekKey, deepseekKeyFocused]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(formDirty);
  }, [formDirty, onDirtyChange]);

  // Si aún no está en Firebase, mostrar y persistir el default (90 s) al guardar.
  useEffect(() => {
    if (!draft) return;
    if (draft.requestTimeoutSec === undefined || draft.requestTimeoutSec === null || draft.requestTimeoutSec === '') {
      setField('requestTimeoutSec', REQUEST_TIMEOUT_DEFAULT_SEC);
    }
    if (draft.useCityFares === undefined || draft.useCityFares === null) {
      setField('useCityFares', true);
    }
    if (draft.smartFarePeakMult === undefined || draft.smartFarePeakMult === null || draft.smartFarePeakMult === '') {
      setField('smartFarePeakMult', 1.15);
    }
    if (draft.smartFareNightMult === undefined || draft.smartFareNightMult === null || draft.smartFareNightMult === '') {
      setField('smartFareNightMult', 1.2);
    }
  }, [draft, setField]);

  const countryValue = useMemo(() => (draft ? countries.find((c) => c.label === draft.country) || null : null), [countries, draft]);
  const restrictValue = useMemo(() => (draft ? countries.find((c) => c.code === draft.restrictCountry) || null : null), [countries, draft]);

  const handleSave = async () => {
    if (!canEdit) {
      setNotice({ severity: 'info', message: 'Modo demostración: los cambios no se guardan.' });
      return;
    }
    const loginBroken = errors.emailLogin && ('emailLogin' in changes || 'mobileLogin' in changes);
    if (loginBroken || Object.keys(changes).some((k) => errors[k])) {
      setNotice({ severity: 'error', message: 'Revisa los campos marcados en rojo antes de guardar.' });
      return;
    }
    const payload = normalize(changes);
    setDraft((d) => ({ ...d, ...payload }));
    setSaving(true);
    try {
      const tasks = [];
      if (Object.keys(payload).length) tasks.push(dispatch(editSettings(payload)));
      if (deepseekDirty) tasks.push(dispatch(editDeepseekApiKey(deepseekApiKey)));
      await Promise.all(tasks);
      setDeepseekKeyFocused(false);
      const n = Object.keys(payload).length + (deepseekDirty ? 1 : 0);
      setNotice({ severity: 'success', message: `Configuración guardada (${n} cambio${n === 1 ? '' : 's'}).` });
    } catch (e) {
      setNotice({ severity: 'error', message: `No se pudieron guardar los cambios: ${e?.message || e}` });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    discard();
    setDeepseekApiKey(savedDeepseekKey || '');
    setDeepseekKeyFocused(false);
  };

  const checkGeoAdoption = async () => {
    setCheckingAdoption(true);
    try {
      const list = (await dispatch(fetchDrivers('web', true))) || [];
      const withGeo = list.filter((d) => typeof d.location?.geohash === 'string' && d.location.geohash.length >= 5).length;
      setGeoAdoption({ total: list.length, withGeo, pct: list.length ? Math.round((withGeo / list.length) * 100) : 0 });
    } catch (e) {
      setGeoAdoption({ error: e?.message || String(e) });
    } finally {
      setCheckingAdoption(false);
    }
  };

  if (!draft) return <CircularLoading />;

  const toggle = (name, label, description, extra = {}) => (
    <SettingToggle name={name} label={label} description={description} checked={draft[name]} onChange={setField} {...extra} />
  );
  const field = (name, label, extra = {}) => (
    <SettingsField name={name} label={label} value={draft[name]} onChange={setField} error={errors[name]} {...extra} />
  );
  const countryAutocomplete = (label, value, onPick, helper) => (
    <Autocomplete
      options={countries}
      value={value}
      autoHighlight
      disableClearable
      getOptionLabel={(o) => (o ? `${o.label} (${o.code})` : '')}
      isOptionEqualToValue={(o, v) => o.code === v.code}
      onChange={(e, v) => v && onPick(v)}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.code} sx={{ fontFamily: C2_FONT }}>
          {o.label} <Box component="span" sx={{ color: C2.muted, ml: 0.75 }}>{o.code}</Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          margin="dense"
          helperText={helper}
          FormHelperTextProps={{ sx: { fontFamily: C2_FONT, mx: 0.5 } }}
          inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
          sx={settingsFieldSx}
        />
      )}
    />
  );

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      {!canEdit ? <DemoModeAlert /> : null}
      <SettingsNotice notice={notice} onClose={() => setNotice(null)} />
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <SettingsCard icon={PaymentsOutlinedIcon} title="Moneda y pagos" subtitle="Cómo se muestran los precios y qué métodos de pago están disponibles.">
            <Grid container spacing={1.5}>
              <Grid item xs={4}>{field('symbol', 'Símbolo', { required: true, placeholder: '$' })}</Grid>
              <Grid item xs={4}>{field('code', 'Código', { required: true, placeholder: 'COP' })}</Grid>
              <Grid item xs={4}>{field('decimal', 'Decimales', { inputProps: { inputMode: 'numeric' }, helper: 'COP usa 0' })}</Grid>
            </Grid>
            {toggle('swipe_symbol', 'Símbolo después del valor', 'Muestra “5.000 $” en lugar de “$ 5.000”.')}
            {toggle('disable_online', 'Desactivar pagos en línea', 'Oculta la tarjeta y las pasarelas de pago al pedir un viaje.')}
            {toggle('disable_cash', 'Desactivar pagos en efectivo', 'Los viajes solo se podrán pagar con billetera o en línea.')}
            {toggle('disable_tips', 'Desactivar propinas', 'Los usuarios no podrán dejar propina al calificar el viaje.')}
            {showEst && !optionsRequired ? toggle('prepaid', 'Pago anticipado', 'El usuario paga el viaje antes de que empiece.') : null}
            {calcEst && !optionsRequired
              ? toggle('disablesystemprice', 'Desactivar precio del sistema', 'En las ofertas, el conductor propone su propio precio en lugar del calculado por la app.')
              : null}
            {draft.disable_online && draft.disable_cash ? (
              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '12px', fontFamily: C2_FONT }}>
                Pagos en línea y en efectivo están desactivados: los usuarios solo podrán pagar con saldo de billetera.
              </Alert>
            ) : null}
          </SettingsCard>
        </Grid>

        <Grid item xs={12} lg={6} sx={stackedColumnSx}>
          <SettingsCard icon={LoginOutlinedIcon} title="Inicio de sesión" subtitle="Métodos con los que conductores y usuarios entran a la app." sx={{ height: 'auto' }}>
            {toggle('emailLogin', 'Correo y contraseña', 'Registro e ingreso con correo electrónico.')}
            {toggle('mobileLogin', 'Número de celular', 'Ingreso con código de verificación por SMS.')}
            {toggle('socialLogin', 'Redes sociales', 'Ingreso con Google (y Apple en iPhone).')}
            {errors.emailLogin ? (
              <Alert severity="error" sx={{ mt: 1.5, borderRadius: '12px', fontFamily: C2_FONT }}>{errors.emailLogin}</Alert>
            ) : null}
          </SettingsCard>
          <SettingsCard
            icon={VerifiedUserOutlinedIcon}
            title="Seguridad y aprobaciones"
            subtitle="Controles antes de que un conductor o vehículo pueda operar."
            sx={{ mt: 2, height: 'auto', flex: 1 }}
          >
            {toggle('otp_secure', 'Código PIN para iniciar el viaje', 'El usuario le da un código al conductor para comenzar el viaje.')}
            {toggle('driver_approval', 'Aprobar conductores manualmente', 'Los conductores nuevos quedan inactivos hasta que un administrador los apruebe.')}
            {toggle('carApproval', 'Aprobar vehículos manualmente', 'Cada vehículo nuevo necesita aprobación antes de usarse.')}
            {toggle('imageIdApproval', 'Verificar documento de identidad', 'Pasajeros: opcional (insignia Verificado si apruebas). Conductores: obligatorio para operar.')}
          </SettingsCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SettingsCard icon={AccountBalanceWalletOutlinedIcon} title="Billetera y referidos" subtitle="Bonos y montos sugeridos en la app.">
            {field('bonus', 'Bono por referido', { inputProps: numberInput, helper: 'Valor que recibe quien refiere a un nuevo usuario.' })}
            {field('walletMoneyField', 'Montos rápidos de recarga', { placeholder: '10000,20000,50000', helper: 'Separados por coma. Aparecen como botones al recargar la billetera.' })}
            {field('tipMoneyField', 'Montos rápidos de propina', { placeholder: '2000,5000,10000', helper: 'Separados por coma. Aparecen al calificar el viaje.' })}
            {toggle('RiderWithDraw', 'Retiros para usuarios', 'Permite que los usuarios, además de los conductores, retiren saldo de su billetera.')}
          </SettingsCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SettingsCard icon={LocalTaxiOutlinedIcon} title="Conductores" subtitle="Asignación de viajes, saldo y requisitos de registro.">
            {draft.autoDispatch !== true ? (
              <Alert
                severity="error"
                sx={{ mb: 1, borderRadius: '12px', fontFamily: C2_FONT }}
                action={
                  <Button color="inherit" size="small" onClick={() => setField('autoDispatch', true)} sx={{ fontFamily: C2_FONT, fontWeight: 700, textTransform: 'none' }}>
                    Activar
                  </Button>
                }
              >
                La asignación automática está apagada: los viajes nuevos no se ofrecen a ningún conductor y esta versión no tiene asignación manual.
              </Alert>
            ) : null}
            {field(
              'driverRadius',
              draft.convert_to_mile ? 'Ola 1 — radio (millas)' : 'Ola 1 — radio (km)',
              {
                inputProps: numberInput,
                helper: 'Primera ola al pedir el viaje. Este valor se respeta tal cual (ya no se fuerza a 15 km).',
              },
            )}
            {field(
              'dispatchWave2AfterMin',
              'Ola 2 — desde (minutos)',
              {
                inputProps: { inputMode: 'numeric' },
                helper: `Minutos sin conductor para ampliar. Vacío = ${DEFAULT_WAVE2_AFTER_MIN}.`,
              },
            )}
            {field(
              'dispatchWave2Radius',
              draft.convert_to_mile ? 'Ola 2 — radio (millas)' : 'Ola 2 — radio (km)',
              {
                inputProps: numberInput,
                helper: `Vacío = ola 1 × ${DEFAULT_WAVE2_FACTOR} (ej. ${resolveDriverRadius(draft)} → ${(resolveDriverRadius(draft) * DEFAULT_WAVE2_FACTOR).toFixed(1)}).`,
              },
            )}
            {field(
              'dispatchWave3AfterMin',
              'Ola 3 — desde (minutos)',
              {
                inputProps: { inputMode: 'numeric' },
                helper: `Minutos sin conductor para la 3.ª ampliación. Vacío = ${DEFAULT_WAVE3_AFTER_MIN}.`,
              },
            )}
            {field(
              'dispatchWave3Radius',
              draft.convert_to_mile ? 'Ola 3 — radio (millas)' : 'Ola 3 — radio (km)',
              {
                inputProps: numberInput,
                helper: `Vacío = ola 1 × ${DEFAULT_WAVE3_FACTOR} (ej. ${resolveDriverRadius(draft)} → ${(resolveDriverRadius(draft) * DEFAULT_WAVE3_FACTOR).toFixed(1)}).`,
              },
            )}
            <SettingsField
              name="requestTimeoutSec"
              label="Contador de cancelación (segundos)"
              value={
                draft.requestTimeoutSec === '' || draft.requestTimeoutSec === undefined || draft.requestTimeoutSec === null
                  ? REQUEST_TIMEOUT_DEFAULT_SEC
                  : draft.requestTimeoutSec
              }
              onChange={setField}
              error={errors.requestTimeoutSec}
              inputProps={{ inputMode: 'numeric' }}
              helper={`Tiempo del contador mientras se busca conductor. Al llegar a 0 se cancela la solicitud. Por defecto ${REQUEST_TIMEOUT_DEFAULT_SEC} s. Rango ${REQUEST_TIMEOUT_MIN_SEC}–${REQUEST_TIMEOUT_MAX_SEC}.`}
            />
            {toggle('negativeBalance', 'Permitir saldo negativo', 'El conductor puede seguir recibiendo viajes aunque su billetera quede en negativo.')}
            {field('driverThreshold', 'Límite de saldo negativo', {
              inputProps: numberInput,
              placeholder: '-20000',
              helper: 'Con saldo negativo activo, al llegar a este saldo o menos deja de recibir viajes pagados con billetera o tarjeta (efectivo, Nequi y transferencia siguen). 0 o vacío = sin límite.',
              sx: { mt: 1 },
            })}
            {toggle('realtime_drivers', 'Conductores en vivo en el mapa', 'El usuario ve en tiempo real los conductores cercanos.')}
            {toggle('showLiveRoute', 'Mostrar ruta en vivo', 'Dibuja la ruta del conductor durante todo el viaje.')}
            {toggle(
              'liveRouteTraffic',
              'Tiempo de llegada con tráfico',
              'La ruta en vivo usa el tráfico actual para calcular el tiempo. Google cobra el doble por esas consultas (2–4 por viaje); las cotizaciones no cambian.',
              { checked: draft.liveRouteTraffic !== false },
            )}
            {toggle(
              'warmBookingDispatch',
              'Instancia caliente al crear viaje',
              'Mantiene una instancia de Cloud Functions siempre encendida para notificar/despachar más rápido al crear un viaje. Apagada = ahorro (~US$15–40/mes); la primera solicitud puede tardar 1–3 s más (cold start). Por defecto apagada.',
              { checked: draft.warmBookingDispatch === true },
            )}
            {toggle('CarHornRepeat', 'Repetir sonido de viaje nuevo', 'La alerta suena hasta que el conductor responde.')}
            {toggle('carType_required', 'Vehículo obligatorio', 'El conductor debe registrar un vehículo para trabajar.')}
            {toggle('license_image_required', 'Foto de la licencia obligatoria', 'Se pide la licencia de conducción en el perfil.')}
            {toggle('bank_fields', 'Datos bancarios en el perfil', 'Pide banco y número de cuenta para los retiros.')}
            {toggle('term_required', 'Aceptar términos al registrarse', 'El registro exige aceptar los términos y condiciones.')}
          </SettingsCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SettingsCard
            icon={PriceChangeOutlinedIcon}
            title="Tarifas por ciudad"
            subtitle="Activa tarifas locales y ajustes por horario. Las tablas de cada ciudad se editan en Zonificación."
          >
            {toggle(
              'useCityFares',
              'Usar tarifas de la ciudad del viaje',
              'Si está apagado, el estimado usa solo las tarifas globales de Tipos de vehículo, aunque la ciudad tenga precios propios.',
              { checked: draft.useCityFares !== false },
            )}
            {toggle(
              'smartFareEnabled',
              'Ajuste inteligente (hora pico / noche)',
              'Multiplica el estimado en horas pico (7–9 y 17–20) y nocturnas (22–5), hora local del dispositivo.',
            )}
            {draft.smartFareEnabled === true ? (
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  {field('smartFarePeakMult', 'Multiplicador hora pico', {
                    inputProps: numberInput,
                    helper: 'Ej. 1.15 = +15 % en pico.',
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {field('smartFareNightMult', 'Multiplicador noche', {
                    inputProps: numberInput,
                    helper: 'Ej. 1.20 = +20 % de noche.',
                  })}
                </Grid>
              </Grid>
            ) : null}
            {toggle(
              'smartFareBidGuard',
              'Anclar ofertas al estimado de ciudad',
              'Las ofertas del pasajero y el piso de puja se calculan sobre el estimado con tarifa de ciudad (y ajuste inteligente si está activo).',
            )}
            {toggle(
              'fareMarketAdvisorEnabled',
              'Cálculo inteligente de tarifas (DeepSeek)',
              'Permite al admin calcular y activar tarifas de vehículos activos. No corre sola: solo desde Tarifas mercado.',
            )}
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label="API key de DeepSeek"
              name="deepseekApiKey"
              autoComplete="off"
              type={!deepseekKeyFocused && deepseekApiKey && deepseekApiKey === savedDeepseekKey ? 'password' : 'text'}
              value={canEdit ? deepseekApiKey : (savedDeepseekKey ? '••••••••••••' : '')}
              disabled={!canEdit}
              placeholder="sk-…"
              helperText="Se guarda en admin_secrets (solo admin), igual que las keys de métodos de pago. Haz clic en el campo para cambiarla."
              FormHelperTextProps={{ sx: { fontFamily: C2_FONT, mx: 0.5 } }}
              sx={{ ...settingsFieldSx, mt: 1.5 }}
              onFocus={() => {
                if (!canEdit) return;
                setDeepseekKeyFocused(true);
                setDeepseekApiKey('');
              }}
              onBlur={() => {
                if (!deepseekApiKey.trim() && savedDeepseekKey) {
                  setDeepseekApiKey(savedDeepseekKey);
                  setDeepseekKeyFocused(false);
                }
              }}
              onChange={(e) => setDeepseekApiKey(e.target.value)}
              InputLabelProps={{ style: { fontFamily: C2_FONT } }}
            />
            {draft.fareMarketAdvisorEnabled === true ? (
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', color: C2.muted, mt: 1 }}>
                Ejecutar en{' '}
                <Box component="a" href="/tarifas-mercado" sx={{ color: C2.violet, fontWeight: 600 }}>
                  Tarifas mercado
                </Box>
                {' '}→ Calcular y activar (home web + app).
              </Typography>
            ) : null}
          </SettingsCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SettingsCard icon={PublicOutlinedIcon} title="País y mapa" subtitle="Región de operación, unidades y cálculo de rutas.">
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                {countryAutocomplete('País por defecto', countryValue, (v) => setField('country', v.label), 'Prefijo telefónico inicial.')}
              </Grid>
              <Grid item xs={12} sm={6}>
                {countryAutocomplete('Restringir direcciones a', restrictValue, (v) => setField('restrictCountry', v.code), 'Las búsquedas de direcciones se limitan a este país.')}
              </Grid>
            </Grid>
            {toggle('AllowCountrySelection', 'Permitir elegir país', 'Deja cambiar el prefijo telefónico en el registro y el perfil.')}
            {toggle('convert_to_mile', 'Distancias en millas', 'Si está apagado se usan kilómetros.')}
            {toggle('useDistanceMatrix', 'Usar Distance Matrix de Google', 'Calcula distancia y tiempo reales por carretera (consume cuota de Google).')}
            {toggle('horizontal_view', 'Lista de vehículos horizontal', 'Muestra los tipos de vehículo en un carrusel horizontal en el mapa.')}
          </SettingsCard>
        </Grid>

        <Grid item xs={12} lg={6} sx={stackedColumnSx}>
          <SettingsCard icon={SosOutlinedIcon} title="Emergencias" subtitle="Número que marca el botón SOS de la app." sx={{ height: 'auto' }}>
            {field('panic', 'Número de emergencia', { placeholder: '123', inputProps: { inputMode: 'tel' } })}
          </SettingsCard>
          <SettingsCard icon={GavelOutlinedIcon} title="Ofertas del usuario" subtitle="Cuando el usuario propone el precio del viaje." sx={{ mt: 2, height: 'auto', flex: 1 }}>
            {toggle('coustomerBidPrice', 'Desactivar oferta del usuario', 'El usuario ya no puede proponer su propio precio.')}
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  margin="dense"
                  label="Tipo de rebaja"
                  value={draft.coustomerBidPriceType || ''}
                  onChange={(e) => setField('coustomerBidPriceType', e.target.value)}
                  helperText=" "
                  sx={settingsFieldSx}
                >
                  <MenuItem value="percentage" sx={{ fontFamily: C2_FONT }}>Porcentaje</MenuItem>
                  <MenuItem value="flat" sx={{ fontFamily: C2_FONT }}>Valor fijo</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                {field('bidprice', draft.coustomerBidPriceType === 'percentage' ? 'Rebaja máxima (%)' : 'Rebaja máxima', { inputProps: numberInput })}
              </Grid>
            </Grid>
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>
              Cuánto puede ofrecer el usuario por debajo del precio estimado.
            </Typography>
          </SettingsCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SettingsCard
            icon={TravelExploreOutlinedIcon}
            title="Búsqueda de conductores"
            subtitle="Cómo encuentra la app a los conductores cercanos al pasajero. Los cambios llegan en la siguiente consulta del mapa."
          >
            {toggle(
              'driversGeoIndexOnly',
              'Búsqueda por zonas (geohash)',
              'Busca solo en las zonas alrededor del pasajero en vez de leer la ubicación de todos los conductores. Reduce mucho la descarga, pero los conductores con versiones viejas de la app dejan de aparecer en el mapa y de recibir viajes.',
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={checkGeoAdoption}
                disabled={checkingAdoption}
                sx={{ fontFamily: C2_FONT, fontWeight: 700, textTransform: 'none', borderRadius: '10px' }}
              >
                {checkingAdoption ? 'Revisando…' : 'Revisar conductores conectados'}
              </Button>
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted, flex: 1, minWidth: 200 }}>
                Muestra cuántos conductores conectados ya usan la versión de la app compatible con la búsqueda por zonas.
              </Typography>
            </Box>
            {geoAdoption?.error ? (
              <Alert severity="error" sx={{ mb: 1, borderRadius: '12px', fontFamily: C2_FONT }}>
                No se pudo revisar: {geoAdoption.error}
              </Alert>
            ) : null}
            {geoAdoption && !geoAdoption.error ? (
              <Alert
                severity={geoAdoption.total === 0 ? 'info' : geoAdoption.pct >= GEOHASH_READY_PCT ? 'success' : 'warning'}
                sx={{ mb: 1, borderRadius: '12px', fontFamily: C2_FONT }}
              >
                {geoAdoption.total === 0
                  ? 'No hay conductores conectados en este momento. Revisa en una hora de más movimiento.'
                  : `${geoAdoption.withGeo} de ${geoAdoption.total} conductores conectados (${geoAdoption.pct} %) usan la versión compatible. ${
                    geoAdoption.pct >= GEOHASH_READY_PCT
                      ? 'Se puede activar la búsqueda por zonas.'
                      : `Conviene esperar a que sean al menos el ${GEOHASH_READY_PCT} %: los demás no aparecerían en el mapa.`
                  }`}
              </Alert>
            ) : null}
            {draft.driversGeoIndexOnly === true && settings?.driversGeoIndexOnly !== true ? (
              <Alert severity="warning" sx={{ mb: 1, borderRadius: '12px', fontFamily: C2_FONT }}>
                Antes de guardar, revisa que al menos el {GEOHASH_READY_PCT} % de los conductores conectados use la versión compatible. Si algo falla, apágala y guarda: el cambio se revierte en la siguiente consulta del mapa.
              </Alert>
            ) : null}
            {field('denseMinDrivers', 'Conductores mínimos en zona densa', {
              inputProps: { inputMode: 'numeric' },
              placeholder: String(DENSE_MIN_DRIVERS),
              helper: `Solo con búsqueda por zonas. Si hay al menos esta cantidad de conductores a ~1 km del pasajero, la búsqueda no se amplía. Un valor más alto envía las solicitudes a conductores más lejanos y descarga algo más de datos. Entre ${DENSE_MIN_DRIVERS_RANGE.min} y ${DENSE_MIN_DRIVERS_RANGE.max}; vacío = ${DENSE_MIN_DRIVERS}.`,
              sx: { mt: 1 },
            })}
          </SettingsCard>
        </Grid>
      </Grid>
      <SettingsSaveBar dirty={formDirty} saving={saving} onSave={handleSave} onDiscard={handleDiscard} disabled={!canEdit} />
    </form>
  );
};

export default GeneralSettings;
